/**
 * The Share dialog's answers that act at once: the owner's «Requests» (demo only), your own «Leave project», and the
 * dialog's toast that says what they did.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ShareRequest, ShareSource } from '../../collaboration/share/source';
import { Alert } from '../Alert';
import { ParticipantAvatar } from './CollaborationOverlay';
import { useAlive } from './useAlive';
import { t } from '../../i18n/I18n';

const TOAST_MS = 2600;

/** Who asks for access (Figma 818:9385): «Approve» grants what they asked at once, «Deny» drops the request; either is final. */
export function RequestsSection({ source, requests, say }: { source: ShareSource; requests: readonly ShareRequest[]; say: (text: string) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const alive = useAlive();
  const answer = async (request: ShareRequest, approve: boolean) => {
    if (busy) return;
    setBusy(request.id);
    try {
      await (approve ? source.approve(request.id) : source.deny(request.id));
      if (approve) say(t(request.wants === 'editor' ? '#WebShareRequestApprovedEdit' : '#WebShareRequestApprovedView', request.name));
    } catch { say(t('#SharingUpdateFailed')); }
    if (alive.current) setBusy(null);
  };
  return <section className="share-section share-requests" aria-labelledby="share-requests-title">
    <div className="share-section-head"><h3 id="share-requests-title">{t('#WebShareRequests')}</h3></div>
    <ul className="share-request-list">{requests.map(request => <li key={request.id} className="share-person share-request">
      <span className="share-avatar"><ParticipantAvatar name={request.name} look={request.look} size={36} /></span>
      <span className="share-request-text"><b>{request.name}</b><span>{t(request.wants === 'editor' ? '#WebShareAsksToEdit' : '#WebShareAsksToView')}</span></span>
      <button type="button" className="share-deny" disabled={!!busy} aria-label={t('#WebShareDenyRequest', request.name)} onClick={() => void answer(request, false)}>{t('#WebShareDeny')}</button>
      <button type="button" className="share-approve" disabled={!!busy} aria-busy={busy === request.id || undefined} aria-label={t('#WebShareApproveRequest', request.name)}
        onClick={() => void answer(request, true)}>{t('#WebShareApprove')}</button>
    </li>)}</ul>
  </section>;
}

/** A short word at the dialog's foot, over the buttons, where the editor's own notice would be hidden by the dialog. */
export function useToast() {
  const [toast, setToast] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const say = (text: string) => {
    clearTimeout(timer.current);
    setToast(text);
    timer.current = setTimeout(() => setToast(''), TOAST_MS);
  };
  return { toast, say };
}

/** «Leave project», asked first in the app's words (`LeaveSharedProject`); `name` is yours, read before you are gone from the list. */
export function LeaveAlert({ source, name, say, onLeft, onClose }: {
  source: ShareSource; name: string; say: (text: string) => void; onLeft?: (name: string) => void; onClose: () => void;
}) {
  const [leaving, setLeaving] = useState(false);
  const leave = () => {
    setLeaving(true);
    source.leave().then(() => { onClose(); onLeft?.(name); }, () => { onClose(); say(t('#SomethingWentWrong')); });
  };
  return createPortal(<Alert title={t('#LeaveProject')} text={t('#LeaveProjectConfirm')} className="share-leave-alert"
    secondary={{ label: t('Cancel'), onSelect: onClose }} primary={{ label: t('#LeaveProject'), busy: leaving, onSelect: leave }}
    onClose={() => { if (!leaving) onClose(); }} />, document.body);
}
